import React from 'react';
import styled from 'styled-components';
import { IoMenu, IoLogOut } from 'react-icons/io5';
import { Button, IconButton } from '../Components/UI/Buttons';
import { FlexContainer, Header } from '../Components/UI/Containers';
import { Text } from '../Components/UI/Typography';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../Context/AuthContext';
import { getCompanyLogoUrl } from '../Services/Api/companyService';

// Use the Header component from Containers.js as base
const NavBarContainer = styled(Header)`
  margin-left: ${props => props.theme.spacing.xxl};
  margin-right: ${props => props.theme.spacing.xxl};
  padding: 15px;
  box-shadow: ${props => props.theme.shadows.medium};
  background-color: ${props => props.theme.colors.background};
  z-index: 1000;
  position: ${props => (props.sticky ? 'sticky' : 'relative')};
  border-radius: 10px;
  
  
  @media (max-width: 768px) {
    margin-left: ${props => props.theme.spacing.md};
    margin-right: ${props => props.theme.spacing.md};
  }
`;

const NavContent = styled(FlexContainer).attrs({
  justify: 'space-between',
  align: 'center',
  gap: 'md'
})`
  max-width: 100%;
`;

const LogoContainer = styled(FlexContainer).attrs({
  align: 'center',
  gap: 'md'
})``;

const Logo = styled.img`
  height: 45px;
  width: auto;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const UserSection = styled(FlexContainer).attrs({
  align: 'center',
  gap: 'md'
})`
  @media (max-width: 768px) {
    gap: ${props => props.theme.spacing.sm};
  }
  
  @media (max-width: 576px) {
    display: none;
  }
`;

const UserName = styled(Text).attrs({
  size: 'lg',
  weight: 'semibold'
})`
  margin: 0;
  margin-right: ${props => props.theme.spacing.sm};
  white-space: nowrap;
  display: none;
  
  @media (min-width: 768px) {
    display: block;
  }
`;

const MenuButton = styled(IconButton)`
  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primaryLight || `${props.theme.colors.primary}10`};
  }
`;

const MobileUserInfo = styled(FlexContainer).attrs({
  align: 'center',
  gap: 'xs'
})`
  @media (min-width: 576px) {
    display: none;
  }
`;

const LogoutButton = styled(Button).attrs({
  variant: 'error',
  size: 'small'
})`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const LoginButton = styled(Button).attrs({
  size: 'small'
})``;

const MobileLogoutButton = styled(IconButton).attrs({
  size: 'small'
})`
  color: ${props => props.theme.colors.error};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.errorLight || `${props.theme.colors.error}10`};
  }
`;

const NavBar = ({ 
  toggleSidebar, 
  isLoggedIn = false, 
  userName = '', 
  onLogout 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleLogoClick = () => navigate('/dashboard');
  const handleLoginClick = () => navigate('/login');

  // Get company logo URL if logged in and company_id exists
  const companyId = user?.company?.id;
  const companyLogoUrl = companyId
    ? getCompanyLogoUrl(companyId)
    : 'header.png'; // fallback

  console.log('user:', user);
  console.log('companyLogoUrl:', companyLogoUrl);

  return (
    <NavBarContainer sticky>
      <NavContent>
        <LogoContainer>
          <MenuButton 
            onClick={toggleSidebar} 
            aria-label="Toggle navigation menu"
            title="Toggle Menu"
          >
            <IoMenu size={24} />
          </MenuButton>
          
          <Logo 
            src={companyLogoUrl}
            alt="Company Logo" 
            onClick={handleLogoClick}
            title="Go to Dashboard"
          />
        </LogoContainer>
        
        <UserSection>
          {isLoggedIn ? (
            <>
              <UserName>
                {userName}
              </UserName>
              
              <LogoutButton 
                onClick={onLogout}
                title="Logout from your account"
                aria-label="Logout"
              >
                <IoLogOut size={16} />
                <span>Logout</span>
              </LogoutButton>
            </>
          ) : (
            <LoginButton 
              onClick={handleLoginClick}
              aria-label="Go to login page"
              title="Login to your account"
            >
              Login
            </LoginButton>
          )}
        </UserSection>

        {/* Mobile logout - only visible on small screens when logged in */}
        {isLoggedIn && (
          <MobileUserInfo>
            <MobileLogoutButton 
              onClick={onLogout}
              title="Logout"
              aria-label="Logout from your account"
            >
              <IoLogOut size={20} />
            </MobileLogoutButton>
          </MobileUserInfo>
        )}
      </NavContent>
    </NavBarContainer>
  );
};

// PropTypes for production safety
NavBar.propTypes = {
  toggleSidebar: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool,
  userName: PropTypes.string,
  onLogout: PropTypes.func
};

export default React.memo(NavBar);
